import {
  CheckCircleFilled,
  FilterFilled,
  FilterOutlined,
} from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { Button, Divider, Radio, Space, Tag, Tooltip, Typography } from 'antd';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  BREAKPOINTS,
  DATE_FORMATS,
  LIMIT,
  PLAN_STATUS,
  SORT,
} from '../../../../../../common/constants';
import { AppContext } from '../../../../../../AppContext';
import {
  formatDate,
  getPlayPlanLabel,
  handleOpenUserDrawer,
  humanizeEnum,
  isSalesUser,
} from '../../../../../../common/utils';
import CommonTable from '../../../../../../components/CommonTable';
import { downloadFileFromUrl } from '../../../../../../components/InventoryUnits/utils';
import SearchComponent from '../../../../../../components/SearchComponent';
import LibraryFilterDropdown from './LibraryFilterDropdown';
import { ULTRA_PLAN_SUBSCRIBERS } from '../../../../graphql/Queries';
import {
  ROADMAPS_TABLE_OFFSET,
  ROADMAP_FILE_FILTER,
  SUBSCRIBERS_SORT_FIELDS,
  SUBSCRIPTION_STATUS_FILTER_OPTIONS,
} from '../../../../ultraPlanConstants';
import {
  getAdditionalLibrary,
  getFullName,
  getLibraryRoute,
  getPrimaryLibrary,
  getRoadmapSelectionStats,
} from '../../../../ultraPlanUtils';

const { Text } = Typography;

const SORT_ORDER_TO_API = { ascend: SORT?.ASC, descend: SORT?.DESC };

const HAS_ROADMAP_FILE_VALUE = {
  [ROADMAP_FILE_FILTER?.ATTACHED?.value]: true,
  [ROADMAP_FILE_FILTER?.NOT_ATTACHED?.value]: false,
};

const readListParams = (search) => {
  const params = new URLSearchParams(search);
  const page = parseInt(params?.get('page'), 10);
  const pageSize = parseInt(params?.get('pageSize'), 10);
  const sortField = params?.get('sortField');
  const sortOrder = params?.get('sortOrder');
  const isValidSort =
    SUBSCRIBERS_SORT_FIELDS?.includes(sortField) &&
    !!SORT_ORDER_TO_API?.[sortOrder];
  return {
    searchTerm: params?.get('search') || '',
    fileFilter: params?.get('file') || null,
    status: params?.get('status') || null,
    libraryIds: params?.get('library')?.split(',')?.filter(Boolean) || [],
    sortField: isValidSort ? sortField : null,
    sortOrder: isValidSort ? sortOrder : null,
    current: page > 0 ? page : 1,
    pageSize: pageSize > 0 ? pageSize : LIMIT,
  };
};

const RoadmapsTable = ({ onOpenRoadmap, onOpenSubscriber }) => {
  const {
    dispatch,
    state: {
      currentUser,
      commonPermissions: { isUserPermission, isLibraryPermission },
    },
  } = useContext(AppContext);
  const history = useHistory();
  const location = useLocation();
  const listParams = useMemo(() => readListParams(location?.search), [
    location?.search,
  ]);

  const [subscribers, setSubscribers] = useState([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState(listParams?.searchTerm);
  const [downloadingId, setDownloadingId] = useState(null);
  const [draftFilters, setDraftFilters] = useState({
    status: listParams?.status,
    fileFilter: listParams?.fileFilter,
  });
  const [isLibraryFilterOpen, setIsLibraryFilterOpen] = useState(false);
  const [libraryTags, setLibraryTags] = useState(() =>
    listParams?.libraryIds?.map((id) => ({ key: id })),
  );

  const handleDownloadRoadmap = async (record) => {
    setDownloadingId(record?.id);
    try {
      await downloadFileFromUrl(record?.metaData?.ultraPlanRoadMap);
    } finally {
      setDownloadingId(null);
    }
  };

  const [fetchSubscribers, { loading: isLoading }] = useLazyQuery(
    ULTRA_PLAN_SUBSCRIBERS,
    {
      fetchPolicy: 'network-only',
      onCompleted: (response) => {
        setSubscribers(response?.ultraPlanSubscribers?.data || []);
        setTotal(response?.ultraPlanSubscribers?.count || 0);
      },
      onError() {
        setSubscribers([]);
        setTotal(0);
      },
    },
  );

  useEffect(() => {
    const {
      searchTerm,
      fileFilter,
      status,
      libraryIds,
      sortField,
      sortOrder,
      current,
      pageSize,
    } = listParams;
    fetchSubscribers({
      variables: {
        filter: {
          skip: (current - 1) * pageSize,
          limit: pageSize,
          search: searchTerm || undefined,
          hasRoadmapFile: HAS_ROADMAP_FILE_VALUE?.[fileFilter],
          status: status || undefined,
          libraryIds: libraryIds?.length ? libraryIds : undefined,
        },
        sort: sortField
          ? { field: sortField, order: SORT_ORDER_TO_API?.[sortOrder] }
          : undefined,
      },
    });
  }, [listParams]);

  const updateListParams = (nextParams) => {
    const merged = { ...listParams, ...nextParams };
    const params = new URLSearchParams();
    if (merged?.searchTerm) params?.set('search', merged?.searchTerm);
    if (merged?.fileFilter) params?.set('file', merged?.fileFilter);
    if (merged?.status) params?.set('status', merged?.status);
    if (merged?.libraryIds?.length) {
      params?.set('library', merged?.libraryIds?.join(','));
    }
    if (merged?.sortField && merged?.sortOrder) {
      params?.set('sortField', merged?.sortField);
      params?.set('sortOrder', merged?.sortOrder);
    }
    if (merged?.current > 1) params?.set('page', merged?.current);
    if (merged?.pageSize !== LIMIT) params?.set('pageSize', merged?.pageSize);
    history?.replace({ search: params?.toString() });
  };

  const paginationConfig = useMemo(
    () => ({
      total,
      current: listParams?.current,
      pageSize: listParams?.pageSize,
    }),
    [total, listParams?.current, listParams?.pageSize],
  );

  const getRadioFilterProps = (paramKey, options) => ({
    filterDropdown: ({ confirm }) => (
      <div className="custom-filter-dropdown list-radio-filter">
        <Radio.Group
          options={options}
          value={draftFilters?.[paramKey]}
          onChange={(event) =>
            setDraftFilters((prev) => ({
              ...prev,
              [paramKey]: event?.target?.value,
            }))
          }
        />
        <Divider className="divider-filter" />
        <div className="d-flex justify-center">
          <Button
            size="small"
            className="reset-button"
            onClick={() => {
              setDraftFilters((prev) => ({ ...prev, [paramKey]: null }));
              updateListParams({ [paramKey]: null, current: 1 });
              confirm();
            }}
          >
            Reset
          </Button>
          <Button
            size="small"
            className="common-button ok-button"
            type="primary"
            onClick={() => {
              updateListParams({
                [paramKey]: draftFilters?.[paramKey],
                current: 1,
              });
              confirm();
            }}
          >
            Ok
          </Button>
        </div>
      </div>
    ),
    filterIcon: () =>
      listParams?.[paramKey] ? (
        <FilterFilled className="filter-icon filter-filled" />
      ) : (
        <FilterOutlined className="filter-icon" />
      ),
    onFilterDropdownOpenChange: (open) =>
      open &&
      setDraftFilters((prev) => ({
        ...prev,
        [paramKey]: listParams?.[paramKey],
      })),
  });

  const getSortProps = (field) => ({
    sorter: true,
    sortOrder: listParams?.sortField === field ? listParams?.sortOrder : null,
  });

  const getLibraryFilterProps = () => ({
    filterDropdown: ({ confirm }) => (
      <LibraryFilterDropdown
        open={isLibraryFilterOpen}
        appliedTags={libraryTags}
        onApply={(tags) => {
          setLibraryTags(tags);
          updateListParams({
            libraryIds: tags?.map((tag) => tag?.key),
            current: 1,
          });
          confirm();
        }}
        onReset={() => {
          setLibraryTags([]);
          updateListParams({ libraryIds: [], current: 1 });
          confirm();
        }}
      />
    ),
    filterIcon: () =>
      listParams?.libraryIds?.length ? (
        <FilterFilled className="filter-icon filter-filled" />
      ) : (
        <FilterOutlined className="filter-icon" />
      ),
    onFilterDropdownOpenChange: setIsLibraryFilterOpen,
  });

  const canOpenLibrary = isLibraryPermission && !isSalesUser(currentUser);

  const getLibraryColumn = ({ title, key, getLibrary, filterProps }) => ({
    title,
    dataIndex: key,
    key,
    ellipsis: true,
    width: 250,
    align: 'left',
    className: 'max-width-column',
    onCell: (record) => {
      const library = getLibrary(record?.user);
      const isClickable = canOpenLibrary && !!library?.id;
      return {
        onClick: (e) => {
          e?.stopPropagation();
          if (isClickable) history?.push(getLibraryRoute(library));
        },
        className: isClickable ? 'pointer table-cell' : '',
      };
    },
    render: (_, record) => {
      const library = getLibrary(record?.user);
      if (!library?.name) return '-';
      return (
        <>
          <div className="table-data-with-id">{library?.name}</div>
          {library?.libraryNumber && (
            <div className="font-size-12">{`(${library?.libraryNumber})`}</div>
          )}
        </>
      );
    },
    ...filterProps,
  });

  const columns = [
    {
      title: 'USER NAME',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: 220,
      align: 'left',
      className: 'max-width-column',
      // eslint-disable-next-line no-undef
      fixed: window.innerWidth > BREAKPOINTS.desktop ? 'left' : false,
      onCell: (record) => ({
        onClick: (e) =>
          isUserPermission &&
          record?.user?.id &&
          handleOpenUserDrawer(e, record?.user, dispatch),
        className: `${
          isUserPermission && record?.user?.id && 'pointer table-cell'
        }`,
      }),
      render: (_, record) => {
        const userData = record?.user;
        return (
          <>
            {userData?.firstName || userData?.lastName ? (
              <div className="table-data-with-id">{getFullName(userData)}</div>
            ) : (
              '-'
            )}
            {userData?.userNumber && (
              <div className="font-size-12">{`(${userData?.userNumber})`}</div>
            )}
          </>
        );
      },
    },
    {
      title: 'EMAIL',
      dataIndex: 'email',
      key: 'email',
      width: 220,
      ellipsis: true,
      render: (_, record) => record?.user?.email || '-',
    },
    {
      title: 'CONTACT',
      dataIndex: 'contactNo',
      key: 'contactNo',
      width: 140,
      render: (_, record) => record?.user?.contactNo || '-',
    },
    {
      title: 'PLAN',
      dataIndex: 'plan',
      key: 'plan',
      width: 180,
      ellipsis: true,
      render: (_, record) =>
        (record?.subscriptionPlan &&
          getPlayPlanLabel(record?.subscriptionPlan)) ||
        '-',
    },
    getLibraryColumn({
      title: 'PRIMARY MAPPED LIBRARY NAME',
      key: 'primaryLibrary',
      getLibrary: getPrimaryLibrary,
      filterProps: getLibraryFilterProps(),
    }),
    getLibraryColumn({
      title: 'ADDITIONAL MAPPED LIBRARY NAME',
      key: 'additionalLibrary',
      getLibrary: getAdditionalLibrary,
    }),
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      ...getRadioFilterProps('status', SUBSCRIPTION_STATUS_FILTER_OPTIONS),
      render: (_, record) =>
        PLAN_STATUS?.[record?.status]?.text ||
        (record?.status ? humanizeEnum(record?.status) : '-'),
    },
    {
      title: 'STARTED ON',
      dataIndex: 'startedAt',
      key: 'startedAt',
      width: 150,
      ...getSortProps('startedAt'),
      render: (_, record) =>
        record?.startedAt
          ? formatDate(record?.startedAt, DATE_FORMATS?.REGULAR)
          : '-',
    },
    {
      title: 'EXPIRES ON',
      dataIndex: 'expireAt',
      key: 'expireAt',
      width: 150,
      ...getSortProps('expireAt'),
      render: (_, record) =>
        record?.expireAt
          ? formatDate(record?.expireAt, DATE_FORMATS?.REGULAR)
          : '-',
    },
    {
      title: 'ROADMAP',
      dataIndex: 'roadmap',
      key: 'roadmap',
      width: 170,
      render: (_, record) => {
        const {
          weeksCount,
          assignedCount,
          totalSlots,
        } = getRoadmapSelectionStats(record?.roadmapSelections);
        if (!weeksCount) return <Tag>Not started</Tag>;
        return (
          <Space direction="vertical" size={0}>
            <Tag color={assignedCount === totalSlots ? 'green' : 'orange'}>
              {`${weeksCount} week${weeksCount > 1 ? 's' : ''} planned`}
            </Tag>
            <Text type="secondary" className="ultra-plan-caption">
              {`${assignedCount}/${totalSlots} toys assigned`}
            </Text>
          </Space>
        );
      },
    },
    {
      title: 'ROADMAP ATTACHED',
      dataIndex: 'roadmapFile',
      key: 'roadmapFile',
      width: 200,
      align: 'center',
      ...getRadioFilterProps('fileFilter', Object.values(ROADMAP_FILE_FILTER)),
      render: (_, record) =>
        record?.metaData?.ultraPlanRoadMap ? (
          <Tooltip title="Roadmap file attached">
            <CheckCircleFilled className="roadmap-file-attached" />
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      title: 'ACTIONS',
      dataIndex: 'actions',
      key: 'actions',
      width: 430,
      // eslint-disable-next-line no-undef
      fixed: window.innerWidth > BREAKPOINTS.desktop ? 'right' : false,
      className: 'cursor-auto',
      render: (_, record) => {
        const roadmapFile = record?.metaData?.ultraPlanRoadMap;
        return (
          <div className="action-button">
            <div className="mr-16">
              <Button
                type="primary"
                size="small"
                className="table-action-primary-btn"
                onClick={() => onOpenSubscriber(record)}
              >
                View Details
              </Button>
            </div>
            <div className="mr-16">
              <Button
                type="primary"
                size="small"
                className="table-action-primary-btn"
                onClick={() => onOpenRoadmap(record)}
              >
                Manage Roadmap
              </Button>
            </div>
            <Button
              type="primary"
              size="small"
              className="table-action-primary-btn"
              disabled={!roadmapFile}
              loading={downloadingId === record?.id}
              onClick={() => handleDownloadRoadmap(record)}
            >
              Download Roadmap
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="d-flex justify-end mb-16">
        <SearchComponent
          className="search-box"
          name="by user number, name, email or contact"
          query={query}
          setQuery={setQuery}
          getData={(value) =>
            updateListParams({ searchTerm: value?.trim(), current: 1 })
          }
        />
      </div>
      <CommonTable
        locale={{ emptyText: isLoading ? <span /> : 'No subscribers found' }}
        columns={columns}
        data={subscribers}
        loadingData={isLoading}
        rowKey={(record) => record?.id}
        paginationConfig={paginationConfig}
        setHeight={ROADMAPS_TABLE_OFFSET}
        onChange={(pagination, _filters, sorter) => {
          const sortOrder = sorter?.column ? sorter?.order : null;
          const isSortChanged =
            (sortOrder ? sorter?.field : null) !== listParams?.sortField ||
            sortOrder !== listParams?.sortOrder;
          updateListParams({
            current: isSortChanged ? 1 : pagination?.current,
            pageSize: pagination?.pageSize,
            sortField: sortOrder ? sorter?.field : null,
            sortOrder,
          });
        }}
      />
    </>
  );
};

export default RoadmapsTable;
